import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Users, MapPin, Clock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';

const YogaRetreat = () => {
  const navigate = useNavigate();

  const handleBooking = () => {
    navigate('/');
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  const handleGoBack = () => {
    navigate('/#upcoming-experiences');
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Forest Yoga Retreat",
    "description": "Deepen your practice surrounded by ancient wisdom of the trees. 3-day yoga retreat for all levels in a sacred forest setting.",
    "startDate": "2024-04-12",
    "endDate": "2024-04-14",
    "location": {
      "@type": "Place",
      "name": "Sacred Forest Grove",
      "address": "Beskid Wyspowy, Poland"
    },
    "organizer": {
      "@type": "Organization",
      "name": "Mystic Forest",
      "url": "https://mysticforest.club"
    },
    "offers": {
      "@type": "Offer",
      "price": "485",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      <SEO
        title="Forest Yoga Retreat - Practice Yoga in Nature"
        description="3-day forest yoga retreat for all levels. Morning flows, meditation, nature walks, and restorative evening practices under ancient trees."
        canonicalUrl="https://mysticforest.club/experiences/yoga-retreat"
        jsonLd={jsonLd}
      />
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        {/* Go Back Button */}
        <div className="pt-20 pb-4">
          <div className="max-w-6xl mx-auto px-6">
            <Button 
              variant="ghost" 
              onClick={handleGoBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Experiences
            </Button>
          </div>
        </div>
        
        {/* Hero Section */}
        <section className="relative py-32">
          <div className="absolute inset-0">
            <img 
              src="/lovable-uploads/9ff3b648-af3e-477b-8988-f181f4af08f7.png" 
              alt="Yoga practitioner in forest setting surrounded by nature"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
            <h1 className="text-5xl md:text-6xl font-serif font-light mb-6">
              Forest Yoga Retreat
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              Deepen your practice surrounded by ancient wisdom of the trees
            </p>
            <Button size="lg" className="px-8 py-4 text-lg" onClick={handleBooking}>
              Join the Retreat
            </Button>
          </div>
        </section>

        {/* Quick Info */}
        <section className="py-16 bg-card">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div className="flex flex-col items-center">
                <Calendar className="w-8 h-8 mb-4 text-accent" />
                <h3 className="font-semibold mb-2">When</h3>
                <p className="text-muted-foreground">April 12-14, 2024</p>
              </div>
              <div className="flex flex-col items-center">
                <MapPin className="w-8 h-8 mb-4 text-accent" />
                <h3 className="font-semibold mb-2">Where</h3>
                <p className="text-muted-foreground">Sacred Forest Grove</p>
              </div>
              <div className="flex flex-col items-center">
                <Users className="w-8 h-8 mb-4 text-accent" />
                <h3 className="font-semibold mb-2">For Whom</h3>
                <p className="text-muted-foreground">All levels welcome</p>
              </div>
              <div className="flex flex-col items-center">
                <Clock className="w-8 h-8 mb-4 text-accent" />
                <h3 className="font-semibold mb-2">Duration</h3>
                <p className="text-muted-foreground">3 Days, 2 Nights</p>
              </div>
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-serif font-light mb-8 text-center">
              Yoga as Nature Intended
            </h2>
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p className="mb-6">
                Step away from crowded studios and practice yoga in its most natural setting. Our Forest Yoga Retreat invites you to deepen your connection with both your inner self and the living world around you.
              </p>
              <p className="mb-6">
                Under the canopy of ancient trees, your practice becomes a conversation with nature itself. Feel the earth beneath your mat, breathe the pure forest air, and let the gentle sounds of the woodland guide you into deeper states of presence.
              </p>
              <p className="mb-6">
                This retreat is perfect for all levels, from curious beginners to seasoned practitioners seeking to refresh their practice in a sacred natural environment.
              </p>
            </div>
          </div>
        </section>

        {/* Daily Schedule */}
        <section className="py-16 bg-card">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-serif font-light mb-12 text-center">
              Daily Rhythm
            </h2>
            <div className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle>Morning Practice (7:00 AM)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Gentle morning flow as the forest awakens. Connect with the day's energy through movement and breath.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Meditation & Nature Walk (9:30 AM)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Silent walking meditation through the forest paths, followed by mindful breakfast with locally sourced foods.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Workshop Sessions (2:00 PM)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Yoga philosophy, breathwork, and specialized techniques like tree pose variations using actual trees as supports.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Evening Practice (6:00 PM)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Restorative yoga and yoga nidra under the stars, ending with sound healing using natural forest acoustics.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-serif font-light mb-12 text-center">
              What's Included
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4">Accommodation & Meals</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• 2 nights in forest eco-cabins</li>
                  <li>• 6 vegetarian meals (organic, local)</li>
                  <li>• Herbal teas and healthy snacks</li>
                  <li>• Filtered spring water</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4">Experience</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• 6 yoga sessions (various styles)</li>
                  <li>• Daily meditation practices</li>
                  <li>• Nature connection workshops</li>
                  <li>• Yoga mat and props provided</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Investment */}
        <section className="py-16 bg-card">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-serif font-light mb-8">Investment</h2>
            <Card className="p-8">
              <div className="text-4xl font-semibold mb-4">$485</div>
              <p className="text-muted-foreground mb-6">Per person</p>
              <div className="text-sm text-muted-foreground mb-8">
                <p>Includes accommodation, meals, all sessions</p>
                <p>Early bird discount: $50 off if booked 30 days prior</p>
              </div>
              <Button size="lg" className="w-full" onClick={handleBooking}>
                Reserve Your Spot
              </Button>
            </Card>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default YogaRetreat;
